import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getPublicConfig } from "../../api/config/getPublicConfig";
import { getDossier } from "../../api/dossier/getDossier";
import { getVehicleImages } from "../../api/vehicle/getVehicleImages";
import { getVehicleOptions } from "../../api/vehicle/getVehicleOptions";
import { getVehicleVariant } from "../../api/vehicle/getVehicleVariant";
import APButton from "../../components/buttons/APButton";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import OverwriteQuoteModal from "../../components/modals/OverwriteQuoteModal";
import PageContainer from "../../components/PageContainer";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import {
  isAIVehicleReviewEnabled,
  isAIVehicleSuggestionEnabled,
} from "../../helpers/configuration";
import { buildQuoteQueryString } from "../../helpers/quoteParams";
import { useAIConsent } from "../../hooks/useAIConsent";
import { useFaqs } from "../../hooks/useFaqs";
import { useQuoteLimit } from "../../hooks/useQuoteLimit";
import { useAuth } from "../../providers/AuthProvider";
import APVehicleDetail from "./components/VehicleDetail";

const DossierVehicleDetail = () => {
  const { dossierId, vehicleCode } = useParams();
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const { shouldPrompt: shouldPromptForAIConsent } = useAIConsent();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quoteId") || undefined;
  const hideQuoteButton =
    !!searchParams.get("hideQuoteButton") &&
    searchParams.get("hideQuoteButton") === "true";

  if (!dossierId || !vehicleCode) {
    // TODO: improve client side error messaging
    throw new Error("dossier id or vehicle code param is misisng");
  }

  const vehicleVariant = getVehicleVariant(vehicleCode);
  const vehicleImages = getVehicleImages(vehicleCode);

  const loadOptions = !!vehicleVariant.data?.cevariantId;
  const vehicleOptions = getVehicleOptions(
    vehicleVariant.data?.cevariantId || "",
    {
      disabled: !loadOptions,
    }
  );

  const dossier = getDossier(dossierId);
  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "vehicle-details",
    contextIds: {
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  const configMap = getPublicConfig();

  if (dossier.isError) {
    throw dossier.error;
  }

  if (vehicleVariant.isError) {
    throw vehicleVariant.error;
  }

  if (vehicleImages.isError) {
    throw vehicleImages.error;
  }

  if (vehicleOptions.isError) {
    throw vehicleOptions.error;
  }

  if (configMap.isError) {
    throw configMap.error;
  }

  // Use quote limit hook
  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId ?? null;
  const { cemakeId, cemodelId, year, cevariantId } = vehicleVariant.data ?? {
    cemakeId: null,
    cemodelId: null,
    year: null,
    cevariantId: null,
  };
  const hasCEVehicleIds = !!cemakeId && !!cemodelId && !!year && !!cevariantId;

  const {
    showOverwriteModal,
    handleQuoteAction,
    handleOverwriteConfirm,
    handleOverwriteCancel,
    oldestQuote,
  } = useQuoteLimit({
    dossierId: dossierId || "",
    primaryQuoteId: primaryQuoteId,
    onRedirect: (overwriteQuoteId?: string) => {
      if (vehicleVariant.data) {
        // Build quote parameters using centralized helper
        const quoteParams = {
          makeId: vehicleVariant.data.cemakeId,
          modelId: vehicleVariant.data.cemodelId,
          modelYear: vehicleVariant.data.year,
          variantId: vehicleVariant.data.cevariantId,
          overwriteQuote: overwriteQuoteId,
        };

        const queryString = buildQuoteQueryString(quoteParams);

        if (quoteId) {
          navigate(`${getScopedPageUrl("editQuote", quoteId)}${queryString}`);
        } else if (primaryQuoteId) {
          navigate(
            `${getScopedPageUrl("cloneQuote", primaryQuoteId)}${queryString}`
          );
        } else {
          navigate(
            `${getScopedPageUrl("createQuote", dossierId)}${queryString}`
          );
        }
      }
    },
    onCancel: () => {},
  });

  const onAIReview = async () => {
    navigate({
      pathname: getScopedPageUrl(
        "dossierVehicleReview",
        dossierId,
        vehicleCode
      ),
      search: quoteId ? `?quoteId=${quoteId}` : undefined,
    });
  };

  const onAISuggestions = () => {
    const _quoteId = quoteId || primaryQuoteId;

    if (_quoteId && vehicleVariant.data && hasCEVehicleIds) {
      const queryString = `?makeId=${vehicleVariant.data.cemakeId}&modelId=${vehicleVariant.data.cemodelId}&modelYear=${vehicleVariant.data.year}&variantId=${vehicleVariant.data.cevariantId}`;

      navigate(
        `${getScopedPageUrl(
          "viewQuoteVehicleSuggestions",
          _quoteId
        )}${queryString}`
      );
    }
  };

  const isLoading =
    vehicleImages.isLoading ||
    dossier.isLoading ||
    configMap.isLoading ||
    vehicleVariant.isLoading ||
    (loadOptions && vehicleOptions.isLoading) ||
    isLoadingFaqs;
  const isSuccess =
    vehicleImages.isSuccess &&
    dossier.isSuccess &&
    configMap.isSuccess &&
    vehicleVariant.isSuccess &&
    (!loadOptions || vehicleOptions.isSuccess) &&
    isSuccessFaqs;

  const isVehicleSuggestionsEnabled =
    configMap.isSuccess && isAIVehicleSuggestionEnabled(configMap.data);
  const isVehicleReviewEnabled =
    configMap.isSuccess && isAIVehicleReviewEnabled(configMap.data);

  return (
    <PageContainer
      loading={isLoading}
      title="Vehicle Detail"
      titleButton={
        hasCEVehicleIds &&
        !hideQuoteButton && (
          <APButton
            text="Quote"
            color="secondary"
            variant="contained"
            icon="play"
            onClick={() => handleQuoteAction(!!quoteId)}
          />
        )
      }
    >
      {isSuccess && (
        <>
          <OverwriteQuoteModal
            isOpen={showOverwriteModal}
            onClose={handleOverwriteCancel}
            onConfirm={handleOverwriteConfirm}
            oldestQuote={oldestQuote}
          />
          <APVehicleDetail
            vehicleVariant={vehicleVariant.data}
            vehicleImages={vehicleImages.data}
            vehicleOptions={vehicleOptions.data || null}
            shouldPromptForAIConsent={shouldPromptForAIConsent}
            onAISuggestions={onAISuggestions}
            onAIReview={onAIReview}
            faqProps={faqProps}
            displaySections={{
              images: true,
              specs: true,
              details: true,
              options: false,
              manufacturerOptions: true,
              standardFeatures: true,
              aiReview: isVehicleReviewEnabled,
              aiSuggestions:
                isVehicleSuggestionsEnabled &&
                !!primaryQuoteId &&
                hasCEVehicleIds,
            }}
          />

          <Stack
            spacing={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              mt: 2,
              pt: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                columnGap: 1,
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Box
                  sx={{ display: "flex", columnGap: 1, alignItems: "center" }}
                >
                  <BackButton />
                </Box>
              </Box>
              {hasCEVehicleIds && !hideQuoteButton && (
                <Box>
                  <APButton
                    text="Quote"
                    color="secondary"
                    variant="contained"
                    icon="play"
                    onClick={() => handleQuoteAction(!!quoteId)}
                  />
                </Box>
              )}
            </Box>
            <Box>
              <RestrictedAppScope scope="external">
                <APContactHelpButton
                  regardingOpportunityId={
                    dossier.data.opportunity?.id ?? undefined
                  }
                />
              </RestrictedAppScope>
            </Box>
          </Stack>
        </>
      )}
    </PageContainer>
  );
};

export default DossierVehicleDetail;
